const API_BASE = import.meta.env.VITE_API_URL || '/api';

/* ── Auth token management ─────────────────────────────────── */
let currentAuthToken = null;

export function setAuthToken(token) {
  currentAuthToken = token;
}

function getHeaders(extra = {}) {
  const headers = { 'Content-Type': 'application/json', ...extra };
  if (currentAuthToken) {
    headers['Authorization'] = `Bearer ${currentAuthToken}`;
  }
  return headers;
}

async function handleResponse(res) {
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      msg = body.error || body.message || msg;
    } catch (_) {}
    throw new Error(msg);
  }
  return res.json();
}

/* ── Templates ─────────────────────────────────────────────── */
export async function fetchTemplates({
  category, q, price, minRating, sort, format, page = 1, limit = 20
} = {}) {
  const params = new URLSearchParams();
  if (category && category !== 'All') params.append('category', category);
  if (q)         params.append('q', q);
  if (price)     params.append('price', price);
  if (minRating) params.append('min_rating', minRating);
  if (sort)      params.append('sort', sort);
  if (format)    params.append('format', format);
  params.append('page',  String(page));
  params.append('limit', String(limit));

  const res = await fetch(`${API_BASE}/templates?${params}`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function fetchTemplate(slug) {
  const res = await fetch(`${API_BASE}/templates/${slug}`, { headers: getHeaders() });
  return handleResponse(res);
}

/* ── Categories ────────────────────────────────────────────── */
export async function fetchCategories() {
  const res = await fetch(`${API_BASE}/categories`, { headers: getHeaders() });
  return handleResponse(res);
}

/* ── Designers ─────────────────────────────────────────────── */
export async function fetchDesigner(username) {
  const res = await fetch(`${API_BASE}/designers/${username}`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function fetchDesignerTemplates(username) {
  const res = await fetch(`${API_BASE}/designers/${username}/templates`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function fetchTopDesigners() {
  const res = await fetch(`${API_BASE}/designers/top`, { headers: getHeaders() });
  return handleResponse(res);
}

/* ── Likes ─────────────────────────────────────────────────── */
export async function toggleLike(templateId) {
  const res = await fetch(`${API_BASE}/templates/${templateId}/like`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export async function getLikeStatus(templateId) {
  const res = await fetch(`${API_BASE}/templates/${templateId}/like/status`, {
    headers: getHeaders(),
  });
  return handleResponse(res);
}

/* ── Ratings ───────────────────────────────────────────────── */
export async function submitRating(templateId, score) {
  const res = await fetch(`${API_BASE}/templates/${templateId}/ratings`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ score }),
  });
  return handleResponse(res);
}

export async function getRatingStatus(templateId) {
  const res = await fetch(`${API_BASE}/templates/${templateId}/ratings/status`, {
    headers: getHeaders(),
  });
  return handleResponse(res);
}

/* ── Comments ──────────────────────────────────────────────── */
export async function fetchComments(templateId, page = 1) {
  const params = new URLSearchParams({ page: String(page), limit: '20' });
  const res = await fetch(`${API_BASE}/templates/${templateId}/comments?${params}`, {
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export async function postComment(templateId, { body, promptType, parentId }) {
  const res = await fetch(`${API_BASE}/templates/${templateId}/comments`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ body, prompt_type: promptType, parent_id: parentId }),
  });
  return handleResponse(res);
}

/* ── Downloads ─────────────────────────────────────────────── */
export async function requestDownload(templateId) {
  const res = await fetch(`${API_BASE}/downloads/${templateId}`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export async function fetchLibrary(page = 1) {
  const params = new URLSearchParams({ page: String(page), limit: '20' });
  const res = await fetch(`${API_BASE}/downloads/library?${params}`, { headers: getHeaders() });
  return handleResponse(res);
}

/* ── Auth ──────────────────────────────────────────────────── */
export async function syncUser(userMeta) {
  const res = await fetch(`${API_BASE}/auth/sync`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(userMeta || {}),
  });
  return handleResponse(res);
}

export async function fetchMe() {
  const res = await fetch(`${API_BASE}/auth/me`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function upgradeToDesigner(profileData) {
  const res = await fetch(`${API_BASE}/auth/upgrade-to-designer`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(profileData || {}),
  });
  return handleResponse(res);
}

/* ── Users ─────────────────────────────────────────────────── */
export async function updateProfile(profileData) {
  const res = await fetch(`${API_BASE}/users/profile`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(profileData || {}),
  });
  return handleResponse(res);
}

export async function verifyGhanaCard(file) {
  const formData = new FormData();
  formData.append('file', file);
  
  const headers = getHeaders({});
  delete headers['Content-Type']; // Let browser set boundary automatically for multipart/form-data
  
  const res = await fetch(`${API_BASE}/users/verify-ghana-card`, {
    method: 'POST',
    headers: headers,
    body: formData,
  });
  return handleResponse(res);
}

/* ── Uploads ───────────────────────────────────────────────── */
export async function uploadFile(file, onProgress) {
  const formData = new FormData();
  formData.append('file', file);

  // For progress tracking we use XMLHttpRequest
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/uploads`);
    if (currentAuthToken) xhr.setRequestHeader('Authorization', `Bearer ${currentAuthToken}`);
    if (onProgress) xhr.upload.onprogress = (e) => onProgress(e.loaded / e.total);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText));
      } else {
        try {
          const err = JSON.parse(xhr.responseText);
          reject(new Error(err.error || `Upload failed: ${xhr.status}`));
        } catch (_) {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(formData);
  });
}

export async function createTemplate(payload) {
  const res = await fetch(`${API_BASE}/templates`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

/* ── Views ─────────────────────────────────────────────────── */
export async function recordView(templateId, sessionId) {
  // Fire-and-forget — don't await
  fetch(`${API_BASE}/views`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ template_id: templateId, session_id: sessionId }),
  }).catch(() => {});
}

/* ── Competitions ──────────────────────────────────────────── */
function getFingerprint() {
  let fp = localStorage.getItem('dh_voter_fp');
  if (!fp) {
    fp = 'fp_' + Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
    localStorage.setItem('dh_voter_fp', fp);
  }
  return fp;
}

export async function fetchCompetitions({ status, format_type, category, search } = {}) {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (format_type) params.append('format_type', format_type);
  if (category && category !== 'All') params.append('category', category);
  if (search) params.append('search', search);

  const res = await fetch(`${API_BASE}/competitions?${params}`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function fetchCompetition(idOrSlug) {
  const res = await fetch(`${API_BASE}/competitions/${idOrSlug}`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function submitCompetitionEntry(competitionId, entryData) {
  const res = await fetch(`${API_BASE}/competitions/${competitionId}/entries`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(entryData),
  });
  return handleResponse(res);
}

export async function voteCompetitionEntry(competitionId, { entry_id, score = 1, critique = '' }) {
  const fingerprint = getFingerprint();
  const res = await fetch(`${API_BASE}/competitions/${competitionId}/vote`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ entry_id, score, critique, fingerprint }),
  });
  return handleResponse(res);
}

/* ── Community ─────────────────────────────────────────────── */
export async function fetchCommunityPosts({ tag, limit = 20, offset = 0 } = {}) {
  const params = new URLSearchParams();
  if (tag) params.append('tag', tag);
  params.append('limit', String(limit));
  params.append('offset', String(offset));

  const res = await fetch(`${API_BASE}/community/posts?${params}`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function createCommunityPost({ content, media_url, media_type, tags }) {
  const res = await fetch(`${API_BASE}/community/posts`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ content, media_url, media_type, tags }),
  });
  return handleResponse(res);
}

export async function togglePostLike(postId) {
  const res = await fetch(`${API_BASE}/community/posts/${postId}/like`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export async function fetchPostComments(postId) {
  const res = await fetch(`${API_BASE}/community/posts/${postId}/comments`, {
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export async function addPostComment(postId, content) {
  const res = await fetch(`${API_BASE}/community/posts/${postId}/comments`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ content }),
  });
  return handleResponse(res);
}

/* ── Competition Enrollment & Auditions ────────────────────── */
export async function enrollInCompetition(competitionId) {
  const res = await fetch(`${API_BASE}/competitions/${competitionId}/enroll`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return handleResponse(res);
}
export const enrollCompetition = enrollInCompetition;

export async function submitCompetitionAudition(competitionId, { audition_portfolio_url, audition_note }) {
  const res = await fetch(`${API_BASE}/competitions/${competitionId}/audition`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ audition_portfolio_url, audition_note }),
  });
  return handleResponse(res);
}

/* ── Cart & Orders ─────────────────────────────────────────── */
export async function fetchCart() {
  const res = await fetch(`${API_BASE}/cart`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function addToCart(templateId) {
  const res = await fetch(`${API_BASE}/cart`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ template_id: templateId }),
  });
  return handleResponse(res);
}

export async function removeFromCart(templateId) {
  const res = await fetch(`${API_BASE}/cart/${templateId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export async function clearCart() {
  const res = await fetch(`${API_BASE}/cart`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export async function checkoutCart(payment_method = 'card') {
  const res = await fetch(`${API_BASE}/cart/checkout`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ payment_method }),
  });
  return handleResponse(res);
}

export async function fetchOrders() {
  const res = await fetch(`${API_BASE}/orders`, { headers: getHeaders() });
  return handleResponse(res);
}

/* ── Direct Messages (Chat Designer) ────────────────────────── */
export async function fetchMessages(recipientId) {
  const res = await fetch(`${API_BASE}/messages/${recipientId}`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function sendMessage(recipientId, content, attachmentUrl = null) {
  const res = await fetch(`${API_BASE}/messages`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ recipient_id: recipientId, content, attachment_url: attachmentUrl }),
  });
  return handleResponse(res);
}

export async function fetchConversations() {
  const res = await fetch(`${API_BASE}/messages/conversations`, { headers: getHeaders() });
  return handleResponse(res);
}

/* ── Notifications & VAPID Web Push ─────────────────────────── */
export async function fetchNotifications() {
  const res = await fetch(`${API_BASE}/notifications`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function markNotificationRead(id) {
  const res = await fetch(`${API_BASE}/notifications/${id}/read`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export async function markAllNotificationsRead() {
  const res = await fetch(`${API_BASE}/notifications/read-all`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export async function registerPushSubscription(subscription) {
  const res = await fetch(`${API_BASE}/notifications/subscribe`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(subscription),
  });
  return handleResponse(res);
}

export async function updateDesignerSocials(payload) {
  const res = await fetch(`${API_BASE}/designers/profile/socials`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

/* ── Paystack Payments ─────────────────────────────────────── */
export async function initializePayment(payload = {}) {
  const res = await fetch(`${API_BASE}/payments/initialize`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function verifyPayment(reference) {
  const res = await fetch(`${API_BASE}/payments/verify/${reference}`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return handleResponse(res);
}

/* ── Designer Dashboard & Analytics ────────────────────────── */
export async function fetchDashboardTemplates() {
  const res = await fetch(`${API_BASE}/dashboard/templates`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function fetchDashboardAnalytics() {
  const res = await fetch(`${API_BASE}/dashboard/analytics`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function updateTemplateStatus(templateId, status) {
  const res = await fetch(`${API_BASE}/dashboard/templates/${templateId}/status`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ status }),
  });
  return handleResponse(res);
}

/* ── Forks & Merge Requests ────────────────────────────────── */
export async function forkTemplate(templateId) {
  const res = await fetch(`${API_BASE}/templates/${templateId}/fork`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export async function submitMergeRequest(templateId, payload) {
  const res = await fetch(`${API_BASE}/templates/${templateId}/merge-requests`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function fetchMergeRequests(templateId) {
  const res = await fetch(`${API_BASE}/templates/${templateId}/merge-requests`, {
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export async function reviewMergeRequest(versionId, payload) {
  const res = await fetch(`${API_BASE}/merge-requests/${versionId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

/* ── Boosts & Featured Spotlight ───────────────────────────── */
export async function fetchFeaturedTemplates() {
  const res = await fetch(`${API_BASE}/featured`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function boostTemplate(templateId, duration_days = 7) {
  const res = await fetch(`${API_BASE}/boosts`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ template_id: templateId, duration_days }),
  });
  return handleResponse(res);
}




